import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';

import Iconify from 'src/components/iconify';
import axiosInstance, { endpoints } from 'src/utils/axios';
import { useSnackbar } from 'src/components/snackbar';
import { useAuth } from 'src/hooks/useAuth';
import { useRouter } from 'src/routes/hooks';
import FormProvider, { RHFTextField } from 'src/components/hook-form';

const schema = Yup.object().shape({
  currentPassword: Yup.string().required('Current password is required'),
  newPassword: Yup.string()
    .required('New password is required')
    .min(6, 'Password must be at least 6 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    .required('Confirm new password is required'),
});

export default function ChangePasswordDialog({ open, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const { logout } = useAuth();
  const router = useRouter();

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      await axiosInstance.post(endpoints.auth.changePassword, {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      enqueueSnackbar('Password changed successfully! Please log in again.', {
        variant: 'success',
      });
      handleClose();

      // Auto logout and redirect
      logout();
      router.replace('/');
    } catch (error) {
      enqueueSnackbar(error?.message || error?.response?.data?.message || 'Failed to change password.', { variant: 'error' });
    }
  });

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="xs" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          boxShadow: (theme) => theme.customShadows?.dialog || theme.shadows[24],
          position: 'relative',
        }
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          color: 'text.secondary',
          '&:hover': {
            bgcolor: 'action.hover',
          }
        }}
      >
        <Iconify icon="mingcute:close-line" />
      </IconButton>

      <DialogTitle sx={{ pb: 1, pt: 3 }}>
        <Stack direction="row" alignItems="center">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: '50%',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              mr: 1.5,
            }}
          >
            <Iconify icon="solar:key-bold-duotone" width={22} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Change Password
          </Typography>
        </Stack>
      </DialogTitle>

      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent sx={{ pt: 1.5, pb: 2 }}>
          <Stack spacing={2.5}>
            <RHFTextField
              name="currentPassword"
              label="Current Password"
              type="password"
              placeholder="Enter current password"
            />

            <RHFTextField
              name="newPassword"
              label="New Password"
              type="password"
              placeholder="Enter new password"
            />

            <RHFTextField
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              placeholder="Confirm new password"
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button 
            onClick={handleClose} 
            variant="outlined" 
            color="inherit" 
            disabled={isSubmitting}
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            startIcon={<Iconify icon="solar:key-bold" />}
            sx={{
              borderRadius: 1.5,
            }}
          >
            Update Password
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

ChangePasswordDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

